import { useMemo } from "react";
import { useFormikContext } from "formik";
import { Paper, Stack, Typography, TextField, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { HugeiconsIcon } from "@hugeicons/react";
import { Image02Icon } from "@hugeicons/core-free-icons";
import { useAppSelector } from "@/hooks";
import { getThumbnailImage } from "@/utils/media";
import MediaManager from "@/components/media-manager/MediaManager";

const HeroSection = () => {
  const { values, setFieldValue, handleChange } = useFormikContext<any>();
  const organizationId = useAppSelector((store) => store.ui.activeOrganization);
  const theme = useTheme();

  // Find the background key in settings, default to "background"
  const backgroundKey = useMemo(() => {
    return (
      Object.keys(values.settings ?? {}).find((key) => key.toLowerCase() === "background") ||
      "background"
    );
  }, [values.settings]);

  const backgroundSetting = values.settings?.[backgroundKey];
  const backgroundUrl = useMemo(() => {
    return backgroundSetting?.value
      ? getThumbnailImage({ file: String(backgroundSetting.value), organizationId })
      : "";
  }, [backgroundSetting?.value, organizationId]);

  const handleBackgroundChange = (selection: string[]) => {
    const setting = values.settings?.[backgroundKey] ?? {};
    setFieldValue(`settings.${backgroundKey}`, { ...setting, value: selection[0] });
  };

  const handleBackgroundDelete = () => {
    const newSettings = { ...(values.settings || {}) };
    delete newSettings[backgroundKey];
    setFieldValue('settings', newSettings);
  };

  // Find the banner key in settings (prioritizing banner, header, hero), default to "banner"
  const bannerKey = useMemo(() => {
    const keys = Object.keys(values.settings ?? {});
    const priority = ["banner", "header", "hero"];
    for (const term of priority) {
      const found = keys.find((key) => key.toLowerCase() === term);
      if (found) return found;
    }
    return "banner";
  }, [values.settings]);

  const bannerSetting = values.settings?.[bannerKey];
  const bannerUrl = useMemo(() => {
    return bannerSetting?.value
      ? getThumbnailImage({ file: String(bannerSetting.value), organizationId })
      : "";
  }, [bannerSetting?.value, organizationId]);

  const handleBannerChange = (selection: string[]) => {
    const setting = values.settings?.[bannerKey] ?? {};
    setFieldValue(`settings.${bannerKey}`, { ...setting, value: selection[0] });
  };

  const handleBannerDelete = () => {
    const newSettings = { ...(values.settings || {}) };
    delete newSettings[bannerKey];
    setFieldValue('settings', newSettings);
  };

  // Check if template settings has specific overrides for title/subtitle,
  // otherwise we bind to organization.title and organization.slogan
  const titlePath = useMemo(() => {
    const key = Object.keys(values.settings ?? {}).find(
      (k) => k.toLowerCase() === "titulo" || k.toLowerCase() === "title"
    );
    return key ? `settings.${key}.value` : "organization.title";
  }, [values.settings]);

  const titleValue = useMemo(() => {
    const parts = titlePath.split(".");
    let val = values;
    for (const part of parts) {
      val = val?.[part];
    }
    return val ?? "";
  }, [titlePath, values]);

  const subtitlePath = useMemo(() => {
    const key = Object.keys(values.settings ?? {}).find(
      (k) => k.toLowerCase() === "subtitulo" || k.toLowerCase() === "subtitle"
    );
    return key ? `settings.${key}.value` : "organization.slogan";
  }, [values.settings]);

  const subtitleValue = useMemo(() => {
    const parts = subtitlePath.split(".");
    let val = values;
    for (const part of parts) {
      val = val?.[part];
    }
    return val ?? "";
  }, [subtitlePath, values]);

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      <Stack spacing={3}>
        {/* Section Title with Icon */}
        <Stack direction="row" spacing={2} alignItems="center">
          <HugeiconsIcon icon={Image02Icon} size={32} color={theme.palette.primary.main} />
          <Typography variant="h3">Sección Hero</Typography>
        </Stack>

        <Grid container spacing={3}>
          {/* Fondo de pantalla */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={1}>
              <Typography variant="h4">Fondo de pantalla</Typography>
              <Paper sx={{
                padding: 2,
                overflow: 'hidden',
                borderRadius: '12px',
              }}>
                <MediaManager
                  value={backgroundUrl}
                  onChange={handleBackgroundChange}
                  onDelete={handleBackgroundDelete}
                />
              </Paper>
            </Stack>
          </Grid>

          {/* Banner Principal */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <Stack spacing={1}>
              <Typography variant="h4">Banner Principal</Typography>
              <Paper sx={{
                padding: 2,
                overflow: 'hidden',
                borderRadius: '12px',
              }}>
                <MediaManager
                  value={bannerUrl}
                  onChange={handleBannerChange}
                  onDelete={handleBannerDelete}
                />
              </Paper>
            </Stack>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {/* Título Hero */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Título Hero"
              name={titlePath}
              value={titleValue}
              onChange={handleChange}
            />
          </Grid>

          {/* Subtítulo */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Subtítulo"
              name={subtitlePath}
              value={subtitleValue}
              onChange={handleChange}
            />
          </Grid>
        </Grid>
      </Stack>
    </Paper>
  );
};

export default HeroSection;
